/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `DirectoryCompanyCard` for the Caintra Connect frontend.
 * @module components/Directory/DirectoryCompanyCard
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { SvgIcon } from "@mui/material";
import Caintra_Logo_03 from "../../assets/Caintra_Logo_03.png";
import AssetImage from "../AssetImage";
import {
  getCompanyDescription,
  getCompanyLocationLabel,
} from "../../utils/directory/companyDisplay.js";
import { ReactComponent as CallIcon } from "../../assets/icons/Call.svg";
import { ReactComponent as DateIcon } from "../../assets/icons/Calendar_Today.svg";
import { ReactComponent as LocationIcon } from "../../assets/icons/Location_on.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/Mail.svg";
import { ReactComponent as StarIcon } from "../../assets/icons/Directory/StarIcon.svg";

const COMPANY_SIZE_BADGE_COLORS = {
  1: "#FFC72D",
  2: "#FF8408",
  3: "#26DD75",
  4: "#335CFF",
};

const COMPANY_SIZE_BADGE_TEXT_COLORS = {
  1: "#694D00",
  2: "#6A3500",
  3: "#00672D",
  4: "#FCFCFC",
};

const ContactText = ({ company, children, styles }) => (
  <span
    className={
      company.activo === false ? styles.inactiveContactText : styles.contactText
    }
  >
    {children}
  </span>
);

const CompanyContactInfo = ({
  company,
  companyLocationLabel,
  t,
  styles,
}) => (
  <div className={styles.companyContactInfo}>
    <div className={styles.contactItem}>
      <SvgIcon sx={{ fontSize: "12px", color: "#58595B" }} component={CallIcon} />
      <ContactText company={company} styles={styles}>
        {company?.telefonoDeOficina}
      </ContactText>
    </div>
    <div className={styles.contactItem}>
      <SvgIcon sx={{ fontSize: "12px", color: "#58595B" }} component={MailIcon} />
      <ContactText company={company} styles={styles}>
        {company?.correoElectronicoPrincipal}
      </ContactText>
    </div>
    <div className={styles.contactItem}>
      {companyLocationLabel && (
        <>
          <SvgIcon
            sx={{ fontSize: "12px", color: "#58595B" }}
            component={LocationIcon}
          />
          <ContactText company={company} styles={styles}>
            {companyLocationLabel}
          </ContactText>
        </>
      )}
    </div>
    {company?.fechaAltaSac && (
      <div className={styles.contactItem}>
        <SvgIcon sx={{ fontSize: "12px", color: "#58595B" }} component={DateIcon} />
        <span className={styles.contactText}>{t("pages.directory.memberSince")}</span>
        <ContactText company={company} styles={styles}>
          {new Date(company.fechaAltaSac).toLocaleDateString("en-GB")}
        </ContactText>
      </div>
    )}
  </div>
);

const DirectoryCompanyCard = ({
  company,
  companySizeLabel,
  states,
  towns,
  isDesktop,
  isTablet,
  i18n,
  t,
  styles,
  onCardClick,
}) => {
  const companyLocationLabel = getCompanyLocationLabel(company, states, towns);
  const description = getCompanyDescription(company, i18n.language);

  return (
    <div
      className={
        company.activo === false ? styles.inactiveCompanyCard : styles.companyCard
      }
      key={company.id}
      onClick={() => onCardClick(company)}
      style={{ cursor: "pointer" }}
    >
      <div className={styles.companyHeader}>
        <div>
          <AssetImage
            className={styles.companyLogo}
            src={company.logo || Caintra_Logo_03}
            alt={t("common.logoAlt")}
          />
        </div>
        <div className={styles.companyInfo}>
          <div className={styles.companyNameContainer}>
            <span
              className={
                company.activo === false
                  ? styles.inactiveCompanyName
                  : styles.companyName
              }
            >
              {company?.nombreComercial}
              {(company?.REPSE || company?.comprobanteDeCertificacion) && (
                <StarIcon className={styles.repseStarIcon} />
              )}
            </span>
            <div className={styles.sizeAndProfileContainer}>
              <span
                className={
                  company.activo === false
                    ? styles.inactiveCompanySize
                    : styles.companySize
                }
                style={{
                  backgroundColor:
                    COMPANY_SIZE_BADGE_COLORS[company?.tamanoDeLaEmpresa],
                  color:
                    COMPANY_SIZE_BADGE_TEXT_COLORS[company?.tamanoDeLaEmpresa],
                }}
              >
                {companySizeLabel}
              </span>
              {company?.indicePerfilEncontrado != null && (
                <span className={styles.companyProfile}>
                  {`${t("pages.directory.profile")} ${company.indicePerfilEncontrado}`}
                </span>
              )}
            </div>
          </div>
          {(isDesktop || isTablet) && (
            <CompanyContactInfo
              company={company}
              companyLocationLabel={companyLocationLabel}
              t={t}
              styles={styles}
            />
          )}
        </div>
      </div>
      {!isDesktop && !isTablet && (
        <CompanyContactInfo
          company={company}
          companyLocationLabel={companyLocationLabel}
          t={t}
          styles={styles}
        />
      )}
      <div className={styles.companyDescription}>
        <span className={styles.descriptionText}>{description}</span>
      </div>
    </div>
  );
};

export default DirectoryCompanyCard;
